import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UrgencyBadge, typeIcons } from "@/components/Badges";
import { FilterSelect } from "@/components/FilterSelect";
import DocumentFormModal from "@/components/DocumentFormModal";
import { Search, Pencil, Download, Paperclip, FileText, ChevronDown, MapPin, Eye, Archive, ExternalLink } from "lucide-react";

const TYPE_ORDER = ["statutory", "license", "contract", "litigation", "resolutions", "other"];
const KNOWN = ["statutory", "license", "contract", "litigation", "resolutions"];
const normType = (tp) => (KNOWN.includes(tp) ? tp : "other");

export default function Archived() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { isAdmin, canEditDoc } = useAuth();

  const [filters, setFilters] = useState({ company_id: "all", type: "all", status: "all", urgency: "all", search: "" });
  const [collapsed, setCollapsed] = useState({});
  const [formDoc, setFormDoc] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const toggleSection = (tp) => setCollapsed((s) => ({ ...s, [tp]: !s[tp] }));

  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/companies")).data });
  const companyMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    return p;
  }, [filters]);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["documents", params],
    queryFn: async () => (await api.get("/documents", { params })).data,
  });

  const archivedDocs = documents.filter((d) => d.archived);
  const docById = useMemo(() => Object.fromEntries(documents.map((d) => [d.id, d])), [documents]);

  const openEdit = (d) => { setFormDoc(d); setModalOpen(true); };

  const renderRow = (d) => {
    const repl = d.replacement_id ? docById[d.replacement_id] : null;
    const renewed = d.archive_outcome === "renewed";
    return (
      <tr key={d.id} data-testid={`archived-row-${d.id}`} className="transition-colors hover:bg-secondary/40">
        <td className="px-4 py-3">
          <div className="flex items-center gap-2 font-medium">
            {d.file && <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />}
            {d.title}
          </div>
          {renewed && repl && (
            <button type="button" onClick={() => navigate(`/documents/${repl.id}`)} data-testid={`archived-replacement-link-${d.id}`} className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-horizonte-orange hover:underline">
              <ExternalLink className="h-3 w-3" /> {t("replacedBy")}: {repl.title}
            </button>
          )}
        </td>
        <td className="px-4 py-3 text-muted-foreground">{companyMap[d.company_id] || "-"}</td>
        <td className="px-4 py-3 text-muted-foreground">{d.counterpart || "—"}</td>
        <td className="px-4 py-3 font-mono text-xs">{d.expiry_date || "—"}</td>
        <td className="px-4 py-3">
          <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${renewed ? "border-violet-200 bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300" : "border-slate-200 bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`} data-testid={`archived-outcome-${d.id}`}>
            {renewed ? t("renewed") : t("archivedStatus")}
          </span>
        </td>
        <td className="px-4 py-3 text-muted-foreground">{d.responsible_person || "—"}</td>
        <td className="px-4 py-3 text-muted-foreground">
          {d.physical_location ? (
            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-horizonte-orange" />{d.physical_location}</span>
          ) : "—"}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`view-${d.id}`} onClick={() => navigate(`/documents/${d.id}`)} title={t("viewLabel")}><Eye className="h-4 w-4" /></Button>
            {d.file && (
              <a href={fileUrl(d.file.storage_path)} target="_blank" rel="noreferrer" data-testid={`download-${d.id}`} title={t("download")}>
                <Button size="icon" variant="ghost" className="h-8 w-8"><Download className="h-4 w-4" /></Button>
              </a>
            )}
            {canEditDoc(d.company_id, d.type) && (
              <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-${d.id}`} onClick={() => openEdit(d)} title={t("editLabel")}><Pencil className="h-4 w-4" /></Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          <Archive className="h-7 w-7 text-muted-foreground" /> {t("archivedDocuments")}
        </h1>
        <p className="mt-1 text-muted-foreground">{t("archivedSub")}</p>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="search-input" className="pl-9" placeholder={t("search")} value={filters.search} onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))} />
          </div>
          <FilterSelect testId="filter-company" value={filters.company_id} onChange={(v) => setFilters((s) => ({ ...s, company_id: v }))} placeholder={t("allCompanies")} options={[{ value: "all", label: t("allCompanies") }, ...companies.map((c) => ({ value: c.id, label: c.name }))]} />
          <FilterSelect testId="filter-type" value={filters.type} onChange={(v) => setFilters((s) => ({ ...s, type: v }))} placeholder={t("allTypes")} options={[{ value: "all", label: t("allTypes") }, { value: "statutory", label: t("statutory") }, { value: "license", label: t("license") }, { value: "contract", label: t("contract") }, { value: "litigation", label: t("litigation") }, { value: "resolutions", label: t("resolutions") }, { value: "other", label: t("other") }]} />
          <FilterSelect testId="filter-status" value={filters.status} onChange={(v) => setFilters((s) => ({ ...s, status: v }))} placeholder={t("allStatus")} options={[{ value: "all", label: t("allStatus") }, { value: "active", label: t("active") }, { value: "pending", label: t("pending") }, { value: "renewed", label: t("renewed") }]} />
          <FilterSelect testId="filter-urgency" value={filters.urgency} onChange={(v) => setFilters((s) => ({ ...s, urgency: v }))} placeholder={t("allUrgency")} options={[{ value: "all", label: t("allUrgency") }, { value: "expired", label: t("expired") }, { value: "critical", label: t("critical") }, { value: "warning", label: t("warning") }, { value: "safe", label: t("safe") }]} />
        </div>
      </Card>

      {isLoading ? (
        <Card className="p-10 text-center text-muted-foreground">…</Card>
      ) : archivedDocs.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground" data-testid="no-archived-documents">{t("noArchivedDocuments")}</Card>
      ) : (
        <div className="space-y-6">
          {TYPE_ORDER.map((tp) => {
            const items = archivedDocs.filter((d) => normType(d.type) === tp);
            if (items.length === 0) return null;
            const Icon = typeIcons[tp] || FileText;
            const isOpen = !collapsed[tp];
            return (
              <Card key={tp} className="overflow-hidden" data-testid={`archived-section-${tp}`}>
                <button type="button" onClick={() => toggleSection(tp)} data-testid={`archived-toggle-${tp}`} aria-expanded={isOpen} className="flex w-full items-center gap-2 border-b bg-secondary/40 px-4 py-3 text-left transition-colors hover:bg-secondary">
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                  <Icon className="h-4 w-4 text-horizonte-orange" />
                  <h3 className="font-display text-sm font-semibold uppercase tracking-wide">{t(tp)}</h3>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-muted-foreground">{items.length}</span>
                </button>
                {isOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid={`archived-table-${tp}`}>
                      <thead className="border-b bg-secondary/50">
                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-4 py-3 font-semibold">{t("title")}</th>
                          <th className="px-4 py-3 font-semibold">{t("company")}</th>
                          <th className="px-4 py-3 font-semibold">{t("counterpart")}</th>
                          <th className="px-4 py-3 font-semibold">{t("expiryDate")}</th>
                          <th className="px-4 py-3 font-semibold">{t("status")}</th>
                          <th className="px-4 py-3 font-semibold">{t("responsible")}</th>
                          <th className="px-4 py-3 font-semibold">{t("physicalLocation")}</th>
                          <th className="px-4 py-3 text-right font-semibold">{t("actions")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">{items.map(renderRow)}</tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <DocumentFormModal open={modalOpen} onOpenChange={setModalOpen} doc={formDoc} companies={companies} />
    </div>
  );
}
